import type {
  APIBodyParamsV2,
  ApiTransformationResponseV2,
} from '@highimpact-frontend/api-types';
import { useCallback, useMemo, useRef, type MutableRefObject } from 'react';

import {
  buildCacheKey,
  useDataLoader,
  type RequestParams,
} from '../useDataLoader';
import {
  applyCsvExportOptionsToApiBody,
  applyDecimationProjectParametersToApiBody,
} from './buildCsvExportMeasureRequest';
import { fetchMeasureCsvText } from './fetchMeasureCsv';
import {
  cloneJsonSerializable,
  timeRangeToInterval
} from './measureRequestHelpers';
import type {
  MeasureThresholdMetadata,
  TimeseriesCsvExportRequest,
  TimeseriesDataTransport,
  TimeseriesFetchRequest,
  TimeseriesFetchRequestWithCache,
  TimeseriesTimeRange,
} from './types';

export {
  decimationToApiParam,
  isSimpleTimeRange,
  timeRangeToInterval
} from './measureRequestHelpers';

// -- type definitions --

/** Interface for session cache transport options. */
export interface SessionCacheTransportOptions {
  mainUrl: string;
  mainBody: APIBodyParamsV2;
  detailUrl?: string;
  detailBody?: APIBodyParamsV2;
  tokenKey?: string;
  cacheLimit?: number;
}

const MEASURE_METADATA_CACHE_SUFFIX = '::measureMetadata';

function measureMetadataCacheKey(cacheKey: string): string {
  return `${cacheKey}${MEASURE_METADATA_CACHE_SUFFIX}`;
}

function readMeasureMetadataCache(
  cacheKey: string
): MeasureThresholdMetadata | null {
  const value = sessionStorage.getItem(measureMetadataCacheKey(cacheKey));
  if (!value) {
    return null;
  }

  return JSON.parse(value) as MeasureThresholdMetadata;
}

function writeMeasureMetadataCache(
  cacheKey: string,
  metadata: MeasureThresholdMetadata
): void {
  try {
    sessionStorage.setItem(
      measureMetadataCacheKey(cacheKey),
      JSON.stringify(metadata)
    );
  } catch {
    try {
      sessionStorage.removeItem(measureMetadataCacheKey(cacheKey));
      sessionStorage.setItem(
        measureMetadataCacheKey(cacheKey),
        JSON.stringify(metadata)
      );
    } catch {
      // Metadata cache is optional; ignore quota errors.
    }
  }
}

function extractMeasureMetadata(
  response: ApiTransformationResponseV2
): MeasureThresholdMetadata {
  return {
    thresholds: response.thresholds,
    threshold_breaches: response.threshold_breaches,
  };
}

function createMeasureLoaderOptions<TRow extends object>(
  metadataRef: MutableRefObject<MeasureThresholdMetadata | null>,
  metadataByCacheKeyRef: MutableRefObject<Map<string, MeasureThresholdMetadata>>
) {
  return {
    loadOnInitiate: false as const,
    extractLoadedData: (response: ApiTransformationResponseV2) =>
      response.data as TRow[],
    onResponseLoaded: (
      response: ApiTransformationResponseV2,
      cacheKey: string
    ) => {
      const metadata = extractMeasureMetadata(response);
      metadataByCacheKeyRef.current.set(cacheKey, metadata);
      metadataRef.current = metadata;
      writeMeasureMetadataCache(cacheKey, metadata);
    },
    onCacheRestored: (cacheKey: string) => {
      const metadata =
        metadataByCacheKeyRef.current.get(cacheKey) ??
        readMeasureMetadataCache(cacheKey);
      if (metadata) {
        metadataByCacheKeyRef.current.set(cacheKey, metadata);
        metadataRef.current = metadata;
      }
    },
  };
}

// -- helpers --

/** Build the request parameters for the data loader. */
export function buildLoaderRequestParams(
  request: TimeseriesFetchRequest,
  options: SessionCacheTransportOptions
): RequestParams {
  const detailUrl = options.detailUrl ?? options.mainUrl;
  const detailBody = options.detailBody ?? options.mainBody;
  const url = request.slot === 'overview' ? options.mainUrl : detailUrl;
  const baseBody = request.slot === 'overview' ? options.mainBody : detailBody;
  const isolatedBody = cloneJsonSerializable(baseBody);

  return {
    url,
    body: applyDecimationProjectParametersToApiBody(
      {
        ...isolatedBody,
        time_range: timeRangeToInterval(request.timeRange),
      },
      request.decimation
    ),
  };
}

/** Resolve the time range used for a CSV export when none is provided explicitly. */
export function resolveCsvExportTimeRange(
  request: TimeseriesCsvExportRequest | undefined,
  mainBody: APIBodyParamsV2
): TimeseriesTimeRange {
  if (request?.timeRange) {
    return request.timeRange;
  }

  const timeRange = mainBody.time_range;
  if (!timeRange) {
    throw new Error(
      'CSV export requires a time range when the transport main body has none'
    );
  }

  return timeRange as TimeseriesTimeRange;
}

/**
 * Build the same POST body as the plot's initial overview request,
 * ready for a CSV download.
 */
export function buildCsvExportRequestParams(
  request: TimeseriesCsvExportRequest | undefined,
  options: SessionCacheTransportOptions
): RequestParams {
  const timeRange = resolveCsvExportTimeRange(request, options.mainBody);
  const body = applyCsvExportOptionsToApiBody(
    options.mainBody,
    request,
    timeRange
  );

  return {
    url: options.mainUrl,
    body,
  };
}

// -- main exports --

/**
 * Session-cache implementation of {@link TimeseriesDataTransport}.
 *
 * Uses {@link useDataLoader} internally to fetch the data from the API.
 *
 * @param options - The options for the session cache transport.
 * @returns The session cache transport.
 */
export function useSessionCacheTransport<TRow extends object>(
  options: SessionCacheTransportOptions
): TimeseriesDataTransport<TRow> {
  const {
    mainUrl,
    mainBody,
    detailUrl,
    detailBody,
    tokenKey = 'security_token',
    cacheLimit = 20,
  } = options;

  const mergedSnapshotRef = useRef<TRow[] | null>(null);
  const measureMetadataRef = useRef<MeasureThresholdMetadata | null>(null);
  const measureMetadataByCacheKeyRef = useRef(
    new Map<string, MeasureThresholdMetadata>()
  );
  const inflightRef = useRef<Map<string, Promise<TRow[]>>>(new Map());
  const overviewLoaderRef = useRef<ReturnType<
    typeof useDataLoader<TRow[], ApiTransformationResponseV2>
  > | null>(null);
  const detailLoaderRef = useRef<ReturnType<
    typeof useDataLoader<TRow[], ApiTransformationResponseV2>
  > | null>(null);
  const runtimeStateRef = useRef({
    loading: false,
    error: null as Error | null,
  });

  const measureLoaderOptions = useMemo(
    () =>
      createMeasureLoaderOptions<TRow>(
        measureMetadataRef,
        measureMetadataByCacheKeyRef
      ),
    []
  );

  const overviewLoader = useDataLoader<
    TRow[],
    ApiTransformationResponseV2
  >([], tokenKey, {
    ...measureLoaderOptions,
    cacheLimit,
  });
  const detailLoader = useDataLoader<
    TRow[],
    ApiTransformationResponseV2
  >([], tokenKey, {
    ...measureLoaderOptions,
    cacheLimit,
  });

  overviewLoaderRef.current = overviewLoader;
  detailLoaderRef.current = detailLoader;
  runtimeStateRef.current.loading =
    overviewLoader.loading || detailLoader.loading;
  runtimeStateRef.current.error =
    overviewLoader.error ?? detailLoader.error;

  const transportOptions = useMemo(
    () => ({
      mainUrl,
      mainBody,
      detailUrl,
      detailBody,
    }),
    [detailBody, detailUrl, mainBody, mainUrl]
  );

  const runFetch = useCallback(
    async (request: TimeseriesFetchRequest): Promise<TRow[]> => {
      const params = buildLoaderRequestParams(request, transportOptions);
      const loaderRef =
        request.slot === 'overview' ? overviewLoaderRef : detailLoaderRef;
      const loader = loaderRef.current;
      if (!loader) {
        throw new Error('Session cache transport loader is not initialized');
      }

      // `replace: true` means a fresh network response is wanted
      const force =
        request.type === 'without-cache' ||
        ('replace' in request && request.replace === true);

      const rows = await loader.updateMainRequests([params], { force });
      const cacheKey = buildCacheKey([params]);
      const metadata =
        measureMetadataByCacheKeyRef.current.get(cacheKey) ??
        readMeasureMetadataCache(cacheKey);
      if (metadata) {
        measureMetadataRef.current = metadata;
      }

      return rows;
    },
    [transportOptions]
  );

  const fetch = useCallback(
    (request: TimeseriesFetchRequestWithCache): Promise<TRow[]> => {
      const existing = inflightRef.current.get(request.requestKey);
      if (existing) {
        return existing;
      }

      const promise = runFetch(request).finally(() => {
        inflightRef.current.delete(request.requestKey);
      });

      inflightRef.current.set(request.requestKey, promise);
      return promise;
    },
    [runFetch]
  );

  const getSnapshot = useCallback(() => mergedSnapshotRef.current, []);

  const setMergedData = useCallback((data: TRow[]) => {
    mergedSnapshotRef.current = data;
  }, []);

  const getInflightRequest = useCallback(
    (requestKey: string) => inflightRef.current.get(requestKey),
    []
  );

  const getMeasureMetadata = useCallback(
    () => measureMetadataRef.current,
    []
  );

  const exportCsv = useCallback(
    async (request?: TimeseriesCsvExportRequest): Promise<string> => {
      const params = buildCsvExportRequestParams(request, transportOptions);
      const token = sessionStorage.getItem(tokenKey);

      return fetchMeasureCsvText({
        url: params.url,
        body: params.body,
        token,
      });
    },
    [tokenKey, transportOptions]
  );

  return useMemo(
    () => ({
      fetch,
      exportCsv,
      getSnapshot,
      setMergedData,
      getMeasureMetadata,
      get loading() {
        return runtimeStateRef.current.loading;
      },
      get error() {
        return runtimeStateRef.current.error;
      },
      getInflightRequest,
    }),
    [exportCsv, fetch, getInflightRequest, getMeasureMetadata, getSnapshot, setMergedData]
  );
}
